import React, { useRef, useEffect, useMemo } from 'react';
import { Bold, Italic, Underline, Heading2, List, AlignLeft } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  isDisabled?: boolean;
  errorMessage?: string;
  hint?: string;
  maxLength?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Start typing...',
  minHeight = '200px',
  maxHeight = '400px',
  isDisabled = false,
  errorMessage,
  hint,
  maxLength,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  // Helper function to get text content length (strips HTML tags)
  const getTextContentLength = (html: string): number => {
    if (!html) return 0;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent?.trim().length || 0;
  };

  const textLength = useMemo(() => getTextContentLength(value), [value]);
  const isOverLimit = maxLength ? textLength > maxLength : false;

  // Initialize editor content when value changes externally
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const newHtml = target.innerHTML;
    const newTextLength = getTextContentLength(newHtml);
    
    // If maxLength is set and we're over the limit, prevent further input
    if (maxLength && newTextLength > maxLength) {
      // Restore previous content
      if (editorRef.current) {
        editorRef.current.innerHTML = value || '';
        // Move cursor to end
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      return;
    }
    
    onChange(newHtml);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Prevent input if we're at the limit (except for backspace, delete, arrow keys, etc.)
    if (maxLength && textLength >= maxLength) {
      const allowedKeys = [
        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End', 'Tab', 'Enter'
      ];
      const isModifierKey = e.ctrlKey || e.metaKey || e.altKey;
      
      // Allow formatting shortcuts (Ctrl+B, Ctrl+I, etc.)
      if (isModifierKey) {
        return;
      }
      
      // Allow deletion and navigation keys
      if (allowedKeys.includes(e.key)) {
        return;
      }
      
      // Prevent typing new characters if at limit
      if (e.key.length === 1) {
        e.preventDefault();
      }
    }
  };

  const execCommand = (command: string, valueArg?: string) => {
    if (editorRef.current && !isDisabled) {
      editorRef.current.focus();
      document.execCommand(command, false, valueArg);
      // Trigger onChange after command execution
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
    }
  };

  const formatButtons = [
    { command: 'bold', icon: Bold, title: 'Bold', label: 'B' },
    { command: 'italic', icon: Italic, title: 'Italic', label: 'I' },
    { command: 'underline', icon: Underline, title: 'Underline', label: 'U' },
    { command: 'formatBlock', value: '<h2>', icon: Heading2, title: 'Heading 2', label: 'H2' },
    { command: 'insertUnorderedList', icon: List, title: 'Bullet List', label: '•' },
    { command: 'justifyLeft', icon: AlignLeft, title: 'Align Left', label: '←' },
  ];

  return (
    <div className="flex flex-col space-y-2">
      {label && (
        <p className="text-sm font-extrabold">
          {label}:
        </p>
      )}
      
      {/* Formatting Toolbar */}
      <div className="flex items-center gap-2 p-2 border border-gray-300 rounded-t-lg bg-gray-50">
        {formatButtons.map((btn) => {
          const IconComponent = btn.icon;
          return (
            <button
              key={btn.command}
              type="button"
              className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              onClick={() => execCommand(btn.command, btn.value)}
              disabled={isDisabled}
              title={btn.title}
              aria-label={btn.title}
            >
              {IconComponent ? (
                <IconComponent className="h-4 w-4" />
              ) : (
                <span className="text-sm font-semibold">{btn.label}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Editor */}
      <div 
        className="border border-gray-300 border-t-0 rounded-b-lg relative"
        style={{ 
          resize: 'vertical',
          overflow: 'hidden',
          minHeight,
          maxHeight: '600px',
        }}
      >
        <div
          ref={editorRef}
          contentEditable={!isDisabled}
          className="p-4 focus:outline-none focus:ring-2 focus:ring-primary-500 prose max-w-none h-full overflow-y-auto"
          style={{
            minHeight: '100%',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
          }}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          data-placeholder={placeholder}
          suppressContentEditableWarning
        />
        {/* Character count in bottom right corner */}
        {maxLength && (
          <div className="absolute bottom-2 right-2 text-xs text-gray-500 bg-white/90 px-2 py-1 rounded shadow-sm pointer-events-none z-10">
            <span className={isOverLimit ? 'text-red-600 font-semibold' : ''}>
              {textLength}/{maxLength}
            </span>
          </div>
        )}
      </div>

      {/* Placeholder styling */}
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>

      <div className="flex flex-col">
        {hint && (
          <p className="text-xs text-muted-foreground">
            {hint}
          </p>
        )}
        <div className="flex justify-between items-start text-xs">
          {errorMessage && (
            <p className="text-red-600">{errorMessage}</p>
          )}
          {/* Spacer when no error message but maxLength exists */}
          {!errorMessage && maxLength && <div />}
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;

